import {
  type FC,
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
} from 'react';
import LockIcon from '@assets/img/icons/lock.svg?react';
import { getImage } from '@common/lib/images';
import { type GoodsType } from '@common/types/goods';
import { StringXSmall } from '@components/Typography/Typography';
import { Picture } from '@components/ui/Picture/Picture';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';

import ContractBackground from '@/components/common/ContractBackground/ContractBackground';
import { Container } from '@/components/ui/Container/container';
import { selectProductOfDayByGoodsType } from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

const productItemVariants = cva('relative', {
  variants: {
    variant: {
      light: 'bg-white/10 backdrop-blur-3xl',
      dark: 'bg-black',
      default: 'bg-transparent',
      'dark-60-blur': 'bg-black/60 backdrop-blur-[32px]',
      production: 'bg-black/60 backdrop-blur-3xl',
    },
    radius: {
      default: 'rounded-lg',
      large: 'rounded-2xl',
    },
    status: {
      active: 'border-accent',
      inactive: 'border-white/40',
    },
  },
});

interface GoodsItemProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren,
    VariantProps<typeof productItemVariants> {
  size: {
    width: string;
    height: string;
  };
  kind: GoodsType;
  progress?: number;
}

const GoodsItem = forwardRef<HTMLDivElement, GoodsItemProps>(
  (
    {
      variant,
      radius,
      size: { width, height },
      children,
      kind,
      progress,
      status,
      ...props
    },
    ref,
  ) => {
    const size = parseInt(width) < parseInt(height) ? width : height;
    const goodsImage = getImage({ name: kind, field: 'icons' });
    const productOfDay = useAppSelector((state) =>
      selectProductOfDayByGoodsType(state, kind),
    );

    return (
      <div
        style={{
          width,
          height,
          minWidth: width,
          minHeight: height,
        }}
        ref={ref}
        {...props}
        className={cn(
          productItemVariants({ variant, radius, status }),
          props.className,
          'overflow-hidden',
        )}
      >
        {productOfDay && (
          <ContractBackground
            type={productOfDay.type === 'premium' ? 'purple' : 'green'}
          />
        )}
        {!!progress && (
          <div
            style={{ maxHeight: progress + '%' }}
            className="absolute h-full left-0 bottom-0 z-[9] w-full bg-accent-sec opacity-30 transition-all"
          ></div>
        )}
        <Picture
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ width: size, height: size }}
          img={goodsImage?.img}
          webp={goodsImage?.webp}
          alt={kind}
        />
        {children}
      </div>
    );
  },
);

interface LockGoodsItemProps {
  lvl: number;
  size: {
    width: string;
    height: string;
  };
  classNames?: {
    wrapper?: string;
    icon?: string;
  };
}

const LockGoodsItem: FC<LockGoodsItemProps> = ({ size, lvl, classNames }) => {
  return (
    <Container
      style={{
        width: size.width,
        height: size.height,
      }}
      className={cn('flex items-center justify-center', classNames?.wrapper)}
      variant="black-60"
    >
      <div className="flex flex-col items-center gap-2">
        <LockIcon className={cn('w-6 h-6 shrink-0', classNames?.icon)} />
        <StringXSmall
          weight="bold"
          className="!text-white"
        >{`lvl. ${lvl}`}</StringXSmall>
      </div>
    </Container>
  );
};

export { GoodsItem, LockGoodsItem };
